import { type ScaleLinear } from "d3";
import { debounce } from "lodash-es";
import { equals } from "ramda";
import { PureComponent } from "react";
import { connect } from "react-redux";
import { getURLSearchParamsList } from "widgets/hooks";

import { getExtendedRange, rangeContainsRange, rangeEquals } from "@/app-utils/range.ts";
import { variantShapePileupParams } from "@/app-utils/track-shapes/shapes-settings.ts";
import { estimatePileupCapacity, type IPileup, PileupFactory } from "@/app-utils/track-shapes/track-pileup-factory.ts";
import { getPileupHeight } from "@/app-utils/track-shapes/track-utils.ts";
import {
  type Contig,
  type GenomeBrowserStatistics,
  type Organism,
  type Range,
  type Track,
  type TrackVariants,
} from "@/graphql.ts";
import {
  fetchPhenotypeValues,
  fetchTrackCommunityAnnotations,
  fetchTrackVariants,
  fetchTrackVcfVariants,
  getTrackId,
  type IGenomeViewerStateLayout,
  type IMutantGenome,
  initListOfMutantsFromGenome,
  type ISortableTrack,
  type ITrackData,
  type ITrackVariantInput,
  trackCustomizationChanged,
} from "@/redux/modules/genome-browser.ts";
import { getTableDataSliceFromTrack, type ITableData } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { DEFAULT_VARIANT_TRACK_HEIGHT, TRACK_TYPE } from "@/settings/settings.js";

import { type ITrackCommunityAnnotationsData } from "../features-track/FeatureTrackLoader.js";
import { type SetTooltipContent } from "../GenomeViewerTracksList.js";
import { VariantTrack } from "./VariantTrack.js";

export interface IVariantTrackData extends TrackVariants, ITrackData, ITrackCommunityAnnotationsData {}

export const INITIAL_VARIANT_TRACK_DATA: Partial<IVariantTrackData> = {
  variants: [],
  communityAnnotationsLookup: {},
  communityAnnotationsRange: null,
  communityAnnotationsOverflow: false,
};

interface IVariantTrackLoaderOwnProps {
  track: Track; // track spec
  scale: ScaleLinear<number, number>;
  handleCustomizeVariantsTrack: (trackType: string) => void; // method to call when the "customize" link of Variants track is clicked
  setTooltipContent: SetTooltipContent;
}

interface IVariantTrackLoaderAppStateProps {
  trackData: IVariantTrackData;
  range: Range;
  contig: Contig;
  genome: Organism;
  selectedMutantGenomes: IMutantGenome[];
  layout: IGenomeViewerStateLayout;
  showAnnotations: boolean;
  statistics: GenomeBrowserStatistics;
  refGenomeId: number;
  tableFilterExpression: string;
  preselectedMutantGenomeIds: number[];
}

interface IVariantTrackLoaderDispatchProps {
  initListOfMutantsFromGenome: (preselectedIds?: number[], VCFFlavor?: boolean) => void;
  fetchTrackVariants: (trackVariantsInputParams: ITrackVariantInput) => Promise<TrackVariants>;
  fetchTrackVcfVariants: (trackVariantsInputParams: ITrackVariantInput) => Promise<TrackVariants>;
  fetchTrackCommunityAnnotations: (track: ISortableTrack, contig: Contig, range: Range) => Promise<any>;
  fetchPhenotypeValues: () => Promise<any>;
}

interface IVariantTrackLoaderProps
  extends IVariantTrackLoaderAppStateProps,
    IVariantTrackLoaderDispatchProps,
    IVariantTrackLoaderOwnProps {}

function mapStateToProps(state: IAppState, ownProps: IVariantTrackLoaderOwnProps): IVariantTrackLoaderAppStateProps {
  const tableData: ITableData = getTableDataSliceFromTrack(state.tableData, ownProps.track);

  return {
    refGenomeId: state.auth.session?.params.referenceGenome,
    tableFilterExpression: tableData?.filterExpression,
    layout: state.genomeBrowser.layout,
    showAnnotations: state.auth.session?.params.showCommunityAnnotations,
    statistics: state.genomeBrowser.statistics,

    range: state.genomeBrowser.range,
    contig: state.genomeBrowser.contig,
    genome: state.genomeBrowser.genome,
    trackData: state.genomeBrowser.trackDataMap[getTrackId(ownProps.track)] as IVariantTrackData,
    selectedMutantGenomes: state.genomeBrowser.selectedMutantGenomes,
    preselectedMutantGenomeIds: getURLSearchParamsList("preselected_mutants")?.map((s) => Number.parseInt(s, 10)),
  };
}

function mapDispatchToProps(dispatch): IVariantTrackLoaderDispatchProps {
  return {
    initListOfMutantsFromGenome: (preselectedIds?: number[], VCFFlavor?: boolean) =>
      dispatch(initListOfMutantsFromGenome(preselectedIds, VCFFlavor)),
    fetchTrackVariants: (trackVariantsInputParams: ITrackVariantInput) =>
      dispatch(fetchTrackVariants(trackVariantsInputParams)),
    fetchTrackVcfVariants: (trackVariantsInputParams: ITrackVariantInput) =>
      dispatch(fetchTrackVcfVariants(trackVariantsInputParams)),
    fetchTrackCommunityAnnotations: (track: ISortableTrack, contig: Contig, range: Range) =>
      dispatch(fetchTrackCommunityAnnotations(track, contig, range)),
    fetchPhenotypeValues: () => dispatch(fetchPhenotypeValues()),
  };
}

interface IVariantTrackLoaderState {
  pileup: IPileup;
  dataOverflowEstimation: boolean;
  empty: boolean;
}

class VariantTrackLoader extends PureComponent<IVariantTrackLoaderProps, IVariantTrackLoaderState> {
  private pileupFactory: PileupFactory;

  constructor(props: IVariantTrackLoaderProps) {
    super(props);

    this.state = {
      pileup: null,
      dataOverflowEstimation: true,
      empty: false,
    };
  }

  loadCommunityAnnotationsData = () => {
    if (!this.state.dataOverflowEstimation && this.props.showAnnotations) {
      const extendedRange = getExtendedRange(this.props.contig, this.props.range);
      this.props.fetchTrackCommunityAnnotations(this.props.track, this.props.contig, extendedRange);
    }
  };

  loadData = () => {
    if (!this.state.dataOverflowEstimation) {
      const extendedRange = getExtendedRange(this.props.contig, this.props.range);
      const capacityEstimation = estimatePileupCapacity(this.props.layout.trackWidth, variantShapePileupParams);
      const mutantGenomeIds: number[] = this.props.selectedMutantGenomes.map((genome) => genome.id) || [];

      const trackVariantsInputParams: ITrackVariantInput = {
        track: this.props.track,
        queryRange: extendedRange,
        contig: this.props.contig,
        refGenomeId: this.props.track?.type === TRACK_TYPE.VARIANT ? this.props.refGenomeId : this.props.genome.id,
        limit: capacityEstimation,
        filterExpression: this.props.tableFilterExpression || "",
        mutantGenomeIds: mutantGenomeIds,
      };
      if (this.props.track.type === TRACK_TYPE.VARIANT) {
        this.props.fetchTrackVariants(trackVariantsInputParams);
      } else {
        this.props.fetchTrackVcfVariants(trackVariantsInputParams);
      }
    }
  };

  debouncedLoadData = debounce(this.loadData, 300, { maxWait: 1000 });
  debouncedLoadCommunityAnnotationsData = debounce(this.loadCommunityAnnotationsData, 300, { maxWait: 1000 });

  componentDidMount() {
    this.pileupFactory = new PileupFactory(this.props.scale);

    // init the list of mutant genomes
    this.props.initListOfMutantsFromGenome(
      this.props.preselectedMutantGenomeIds,
      this.props.track.type === TRACK_TYPE.VCF_VARIANT,
    );

    this.props.fetchPhenotypeValues();

    this.setState({
      empty: this.isTrackEmpty(),
      dataOverflowEstimation: estimatePileupCapacity(this.props.layout.trackWidth, variantShapePileupParams) === 0,
    });

    this.debouncedLoadData();
    this.loadCommunityAnnotationsData();
  }

  isTrackEmpty = () => {
    // in theory these tracks are disabled in these cases but it can happen that the track types stick when changing genomes
    if (this.props.track.type === TRACK_TYPE.VCF_VARIANT) {
      // we have no information about the currently selected contig, we have to rely on global statistics
      return !this.props.statistics?.hasVcfGenomeAnalysis;
    }
    if (this.props.track.type === TRACK_TYPE.VARIANT) {
      //  the standard VARIANT track type can counts the number of variants on the current contig, but there is no consolidated count, so for consistency purpose this is fine
      return !this.props.statistics?.hasGenomeAnalysis;
    }
    return false;
  };
  componentDidUpdate(prevProps: Readonly<IVariantTrackLoaderProps>, prevState: Readonly<IVariantTrackLoaderState>) {
    if (this.props.scale !== prevProps.scale) {
      this.pileupFactory = new PileupFactory(this.props.scale);
    }

    const rangeDiffers = !rangeEquals(prevProps.range, this.props.range);
    const contigDiffers: boolean = prevProps.contig?.id !== this.props.contig?.id;
    const boxModelDiffers: boolean = this.props.layout.trackWidth !== prevProps.layout.trackWidth;
    const refGenomeDiffers = this.props.refGenomeId !== prevProps.refGenomeId;
    const statisticsDiffer = this.props.statistics !== prevProps.statistics;
    const dataOverflowEstimationDiffers = this.state.dataOverflowEstimation !== prevState.dataOverflowEstimation;

    if (refGenomeDiffers) {
      this.props.initListOfMutantsFromGenome();
    }

    if (statisticsDiffer) {
      this.setState({ empty: this.isTrackEmpty() });
    }

    if (boxModelDiffers) {
      this.setState({
        dataOverflowEstimation: estimatePileupCapacity(this.props.layout.trackWidth, variantShapePileupParams) === 0,
      });
    }

    const mutantGenomeIds: number[] = this.props.selectedMutantGenomes.map((genome) => genome.id) || [];
    const prevMutantGenomeIds: number[] = prevProps.selectedMutantGenomes.map((genome) => genome.id) || [];
    const mutantGenomesDiffer = !equals(mutantGenomeIds, prevMutantGenomeIds);

    const trackCustomizationDiffers = trackCustomizationChanged(
      this.props.track?.trackCustomization,
      prevProps.track?.trackCustomization,
    );
    // table filters are not part of the track customization, they are stored in the table data
    const tableFilterExpressionDiffers = this.props.tableFilterExpression != prevProps.tableFilterExpression;
    if (
      rangeDiffers ||
      contigDiffers ||
      refGenomeDiffers ||
      trackCustomizationDiffers ||
      tableFilterExpressionDiffers ||
      mutantGenomesDiffer ||
      dataOverflowEstimationDiffers
    ) {
      const missingData: boolean =
        !this.props.trackData?.range ||
        !rangeContainsRange(this.props.trackData?.range, this.props.range) ||
        this.props.trackData?.overflow;
      if (
        contigDiffers ||
        refGenomeDiffers ||
        trackCustomizationDiffers ||
        tableFilterExpressionDiffers ||
        mutantGenomesDiffer ||
        dataOverflowEstimationDiffers
      ) {
        // directly fetch data again
        this.loadData();
      } else if (missingData) {
        // debounced data fetching until range is stabilized
        this.debouncedLoadData();
      }
    }

    const showAnnotationsFlagDiffers: boolean = this.props.showAnnotations !== prevProps.showAnnotations;
    if (contigDiffers || dataOverflowEstimationDiffers || showAnnotationsFlagDiffers) {
      this.loadCommunityAnnotationsData();
    } else if (rangeDiffers) {
      const missingCommunityAnnotationsData: boolean =
        prevProps.trackData?.communityAnnotationsOverflow ||
        !this.props.trackData?.communityAnnotationsRange ||
        !rangeContainsRange(this.props.trackData?.communityAnnotationsRange, this.props.range);
      if (missingCommunityAnnotationsData) {
        this.debouncedLoadCommunityAnnotationsData();
      }
    }

    const trackDataDiffers: boolean = this.props.trackData !== prevProps.trackData;
    // update the layout
    if (trackDataDiffers || rangeDiffers || dataOverflowEstimationDiffers) {
      this.updateLayout();
    } else if (boxModelDiffers) {
      this.debouncedUpdateLayout();
    }

    const trackPhenotypeCustomizationDiffers: boolean =
      this.props.track.trackCustomization?.configuredPhenotypes !==
      prevProps.track.trackCustomization?.configuredPhenotypes;
    if (trackPhenotypeCustomizationDiffers || mutantGenomesDiffer) {
      this.props.fetchPhenotypeValues();
    }
  }

  updateLayout = () => {
    if (!this.props.trackData) {
      return;
    }
    if (this.props.trackData.overflow || !this.props.trackData.variants) {
      this.setState({ pileup: null });
      return;
    }

    const pileup: IPileup = this.pileupFactory.buildVariantPileup(
      this.props.trackData.variants,
      this.props.range,
      variantShapePileupParams,
    );

    this.setState({ pileup: pileup });
  };

  debouncedUpdateLayout = debounce(this.updateLayout, 300, { maxWait: 1000 });

  getTrackHeight = () => {
    const filteredGenomesCount: number = this.props.selectedMutantGenomes?.length || 0;

    let height = getPileupHeight(this.state.pileup);
    if (this.state.pileup) {
      // some space for the message
      const profileMessageHeight: number = this.state.pileup?.variants && filteredGenomesCount === 0 ? 20 : 0;

      const { lanePadding, boxHeight } = this.state.pileup.pileupParams;
      const profileHeight = this.state.pileup?.variants ? filteredGenomesCount * boxHeight + lanePadding : 0;
      height += profileHeight + profileMessageHeight + 7; // TODO something is wrong with the height calculation
    }

    return Math.max(DEFAULT_VARIANT_TRACK_HEIGHT, height);
  };

  render() {
    return (
      <VariantTrack
        pileup={this.state.pileup}
        overflow={this.props.trackData?.overflow}
        track={this.props.track}
        empty={this.state.empty}
        selectedMutantGenomes={this.props.selectedMutantGenomes}
        handleCustomize={this.props.handleCustomizeVariantsTrack}
        filterExpression={this.props.tableFilterExpression}
        trackHeight={this.getTrackHeight()}
        communityAnnotationOverflow={this.props.trackData?.communityAnnotationsOverflow}
        communityAnnotation={this.props.showAnnotations && this.props.trackData?.communityAnnotationsLookup}
        setTooltipContent={this.props.setTooltipContent}
      />
    );
  }
}

const SmartVariantTrackLoader = connect<
  IVariantTrackLoaderAppStateProps,
  IVariantTrackLoaderDispatchProps,
  IVariantTrackLoaderOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(VariantTrackLoader);

export { SmartVariantTrackLoader as VariantTrackLoader };
